import { MUTATE, MUTATE_MULTIPLE } from '@/store/modules/mutation_types';
import PanelsService from '@/services/PanelsService';
import UpmsLegacyService from '@/services/UpmsLegacyService';
import { PANEL_DETAIL_FIELDS, fieldsFromLimits } from './constants';

function cacheUser(userCache, userId) {
  if (!userId) return;
  if (!userCache[userId]) {
    userCache[userId] = UpmsLegacyService.getUser(userId).catch(err => {
      const isResourceNotFound =
        err && err.response &&
        err.response.status === 404 &&
        err.response.data &&
        err.response.data.resource === true;
      if (isResourceNotFound) return { notFound: true, userId };
      return {};
    });
  }
}

function cacheUserByEmail(emailCache, email) {
  if (!email) return;
  if (!emailCache[email]) {
    emailCache[email] = UpmsLegacyService.getUserByEmail(email).catch(err => {
      const isResourceNotFound =
        err && err.response &&
        err.response.status === 404 &&
        err.response.data &&
        err.response.data.resource === true;
      if (isResourceNotFound) return { notFound: true };
      return {};
    });
  }
}

function cachePanelLimits(limitsCache, accountId, universeId) {
  const key = `${accountId}_${universeId}`;
  if (!accountId || !universeId) return;
  if (!limitsCache[key]) {
    limitsCache[key] = PanelsService.getPanelLimits({ accountId, universeId }).catch(() => ({}));
  }
}

function cacheCompany(groupName) {
  if (!groupName || companyCache[groupName]) return;
  companyCache[groupName] = UpmsLegacyService.getUserGroupsWithCS([{ name: groupName }])
    .then(results => (results || []).find(c => c.name === groupName) || {})
    .catch(() => ({}));
}

function preCacheCompanies(groupNames) {
  const uncached = groupNames.filter(n => n && !companyCache[n]);
  if (!uncached.length) return;
  const pending = UpmsLegacyService.getUserGroupsWithCS(uncached.map(name => ({ name }))).catch(() => []);
  uncached.forEach(name => {
    companyCache[name] = pending.then(results => (results || []).find(c => c.name === name) || {});
  });
}

function buildEnrichment(panel, { user = {}, limits = {}, company = null }) {
  return {
    ...panel,
    ...fieldsFromLimits(limits),
    universe_creator_email: user.notFound ? `N/A Encontrado(${user.userId})` : (user.email || ''),
    use_universe_credit_system: company !== null ? (company.useUniverseCreditSystem ?? null) : null,
  };
}

async function enrichPanel(panel, caches) {
  const { userCache, limitsCache } = caches;
  const userId = panel.user_id;
  cacheUser(userCache, userId);
  cachePanelLimits(limitsCache, panel.account_id, panel.universe_id);
  cacheCompany(panel.group);
  const key = `${panel.account_id}_${panel.universe_id}`;
  const [user, limits, company] = await Promise.all([
    userId ? userCache[userId] : Promise.resolve({}),
    limitsCache[key] || Promise.resolve({}),
    panel.group && companyCache[panel.group] ? companyCache[panel.group] : Promise.resolve(null),
  ]);
  return buildEnrichment(panel, { user, limits, company });
}

const userCache = {};
const limitsCache = {};
const emailCache = {};
const panelDetailCache = {};
const companyCache = {};

async function enrichAll(raw) {
  const caches = { userCache, limitsCache };
  const detailCache = {};

  const detailed = await Promise.all(raw.map(async p => {
    const { account_id: accountId, universe_id: universeId } = p;
    const key = `${accountId}_${universeId}`;
    if (!detailCache[key]) {
      detailCache[key] = PanelsService.getPanelDetail({ accountId, universeId, fields: PANEL_DETAIL_FIELDS }).catch(() => ({}));
    }
    const detail = await detailCache[key];
    return { ...p, ...detail };
  }));

  const uniqueGroups = [...new Set(detailed.map(p => p.group).filter(Boolean))];
  preCacheCompanies(uniqueGroups);

  return Promise.all(detailed.map(p => enrichPanel(p, caches)));
}

export async function fetchPanelsByQuery({ commit }, { field, values }) {
  commit(MUTATE_MULTIPLE, [
    { property: 'searchLoading', with: true },
    { property: 'searchError', with: null },
    { property: 'searchResults', with: [] },
  ]);

  try {
    let resolvedField = field;
    let resolvedValues = values;

    if (field === 'usergroup') {
      const accounts = await UpmsLegacyService.getAccountsByUsergroup(values.trim()).catch(() => []);
      const accountIds = (accounts || []).map(a => a.id).filter(id => id !== null && id !== undefined);
      resolvedField = 'acc_id';
      resolvedValues = accountIds.join(',');
    } else if (field === 'usr_id') {
      const emails = values.split(',').map(v => v.trim()).filter(Boolean);
      emails.forEach(email => cacheUserByEmail(emailCache, email));
      const userIds = (await Promise.all(
        emails.map(async email => {
          const user = await emailCache[email];
          if (user && user.notFound) return `N/A Encontrado(${email})`;
          return (user && user.id) ? user.id : null;
        }),
      )).filter(Boolean);
      resolvedValues = userIds.join(',');
    }

    const queryResults = await PanelsService.getPanelsByQuery({
      field: resolvedField,
      values: resolvedValues,
      fields: 'account_id,universe_id,user_id',
    });

    const raw = queryResults || [];
    const panels = await enrichAll(raw);

    commit(MUTATE_MULTIPLE, [
      { property: 'searchResults', with: panels },
      { property: 'isSearchActive', with: true },
    ]);
  } catch (err) {
    commit(MUTATE, { property: 'searchError', with: err });
  } finally {
    commit(MUTATE, { property: 'searchLoading', with: false });
  }
}

export async function clearSearch({ commit }) {
  commit(MUTATE_MULTIPLE, [
    { property: 'searchResults', with: [] },
    { property: 'searchError', with: null },
    { property: 'isSearchActive', with: false },
  ]);
}

export async function fetchPanelDetail({ commit }, { accountId, universeId }) {
  commit(MUTATE_MULTIPLE, [
    { property: 'loadingDetail', with: true },
    { property: 'errorDetail', with: null },
    { property: 'selectedPanel', with: null },
  ]);

  try {
    const cacheKey = `${accountId}_${universeId}`;
    if (!panelDetailCache[cacheKey]) {
      panelDetailCache[cacheKey] = PanelsService.getPanelDetail({ accountId, universeId, fields: PANEL_DETAIL_FIELDS }).catch(err => {
        delete panelDetailCache[cacheKey];
        throw err;
      });
    }
    const data = await panelDetailCache[cacheKey];
    const enriched = await enrichPanel(data, { userCache, limitsCache });
    commit(MUTATE, { property: 'selectedPanel', with: enriched });
  } catch (err) {
    commit(MUTATE, { property: 'errorDetail', with: err });
  } finally {
    commit(MUTATE, { property: 'loadingDetail', with: false });
  }
}

export function invalidatePanelDetailCache(_context, { accountId, universeId }) {
  if (!accountId || !universeId) return;
  const cacheKey = `${accountId}_${universeId}`;
  delete panelDetailCache[cacheKey];
}

export function clearCaches() {
  [userCache, limitsCache, emailCache, panelDetailCache, companyCache].forEach(cache => {
    Object.keys(cache).forEach(key => delete cache[key]);
  });
}
